import {
  Component,
  AfterViewInit,
  Inject,
  NgZone,
  PLATFORM_ID,
  Input,
  SimpleChanges,
  OnChanges,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { DecimalPipe, isPlatformBrowser } from '@angular/common';

import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import * as am5percent from '@amcharts/amcharts5/percent';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { ReportsChartEnum } from 'src/app/reports/models/enums/reports.enum';
import { SharedService } from 'src/app/common/services/shared.service';
import { TranslateService } from '@ngx-translate/core';
import { ReportService } from 'src/app/reports/services/report.service';
import { GlobalContants } from 'src/app/common/constants/global.contants';

@Component({
  selector: 'app-report-charts',
  templateUrl: './report-charts.component.html',
  styleUrls: ['./report-charts.component.scss'],
})
export class ReportChartsComponent
  implements OnChanges, AfterViewInit, OnDestroy, OnInit
{
  @Input() height = '300px';
  @Input() data: any = [];
  @Input() chartType!: number;
  @Input() legend: any = [];
  @Input() isBorder: boolean = true;
  @Input() name: string = 'chartDiv';
  @Input() index: number = 0;
  @Input() text: string = '';
  @Input() is_v2_report = false;
  @Input() staticZoning = false;
  @Input() label: any = '';

  lang!: number;
  private expandedYears: Set<string> = new Set();
  private root!: am5.Root;
  ispercetage: any;
  CurrencyCode: any = '';
  private series!: am5xy.LineSeries;

  private xAxis!: am5xy.CategoryAxis<am5xy.AxisRendererX>;

  constructor(
    @Inject(PLATFORM_ID) private platformId: any,
    private zone: NgZone,
    private sharedService: SharedService,
    private translate: TranslateService,
    private decimalPipe: DecimalPipe,
    public reportService: ReportService,
  ) {}

  ngOnInit() {
    this.lang = this.sharedService.getCurrentLangID();
    this.getChangedLang();
    this.createChart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(changes['data'] && !changes['data'].firstChange) {
       this.browserOnly(() => {
      if (this.root && !this.root.isDisposed()) {
        this.root.dispose();
      }
      this.createChart();
    });
    }

    this.CurrencyCode = this.sharedService.getStoreValue(
      GlobalContants.StoreKey.CurrencyCode,
    );
  }

  ngAfterViewInit() {
    this.browserOnly(() => {
      // this.createChart();
    });
  }
  // Run the function only in the browser
  browserOnly(f: () => void) {
    if (isPlatformBrowser(this.platformId)) {
      this.zone.runOutsideAngular(() => {
        setTimeout(() => {
          f();
        }, 500);
      });
    }
  }

  createChart() {
    if (this.root && !this.root.isDisposed()) {
      this.root.dispose();
    }
    switch (this.chartType) {
      case ReportsChartEnum.barChart:
        this.barChart();
        break;
      case ReportsChartEnum.pieChart:
        this.pieChart();
        break;
      case ReportsChartEnum.barStackedChart:
        this.barStackedChart();
        break;
      case ReportsChartEnum.multiBarLineGroupChart:
        this.multiBarLineGroupChart();
        break;
      case ReportsChartEnum.barLineGroupChart:
        this.barLineGroupChart();
        break;
      case ReportsChartEnum.normalBarLineChart:
        this.normalBarLineChart();
        break;
      case ReportsChartEnum.varianceIndicatorsChart:
        this.varianceIndicatorsChart();
        break;
      case ReportsChartEnum.smallBarChart:
        this.smallBarChart();
        break;
      case ReportsChartEnum.smallvarianceIndicatorsChart:
        this.smallvarianceIndicatorsChart();
        break;
      case ReportsChartEnum.analysisChart:
        this.projectAnalysisChart();
        break;
    }
  }

  getChangedLang() {
    this.sharedService.language$.subscribe((res) => {
      if (res) {
        this.lang = this.sharedService.getCurrentLangID();
      }
    });
  }

  // Bar Chart
  barChart() {
    this.browserOnly(() => {
      if (this.root) {
        this.root.dispose();
      }
      let root = am5.Root.new(`${this.name}`);

      root.setThemes([am5themes_Animated.new(root)]);

      let chart = root.container.children.push(
        am5xy.XYChart.new(root, {
          panX: false,
          panY: false,
          wheelX: 'none',
          wheelY: 'none',
          paddingBottom: 30,
          paddingTop: 0,
          paddingLeft: 0,
          paddingRight: 0,
        }),
      );

      // Define data
      let data = this.data;
      var numberFormatter = am5.NumberFormatter.new(root, {
        numberFormat: '#.#',
      });
      for (var i = 0; i < data.length; i++) {
        data[i].formattedValue = numberFormatter.format(data[i].value);
      }

      let xRenderer = am5xy.AxisRendererX.new(root, {
        minorGridEnabled: true,
        minGridDistance: 30,
      });
      let xAxis = chart.xAxes.push(
        am5xy.CategoryAxis.new(root, {
          categoryField: 'category',
          renderer: xRenderer,
          tooltip: am5.Tooltip.new(root, {}),
        }),
      );
      xRenderer.grid.template.setAll({
        location: 1,
        forceHidden: true,
      });

      //Add Tooltip on label
      xAxis.labelsContainer.set(
        'tooltip',
        am5.Tooltip.new(root, {
          pointerOrientation: 'down',
        }),
      );
      xRenderer.labels.template.setAll({
        oversizedBehavior: 'truncate',
        textAlign: 'center',
        fontSize: 12,
        tooltipText: '{category}',
      });
      xRenderer.labels.template.setup = function (target) {
        target.set(
          'background',
          am5.Rectangle.new(root, {
            fill: am5.color(0x000000),
            fillOpacity: 0,
          }),
        );
      };

      xAxis.onPrivate('cellWidth', function (cellWidth) {
        xRenderer.labels.template.set('maxWidth', cellWidth);
      });

      xAxis.data.setAll(data);

      let yAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          min: 0,
          // maxDeviation: 0.3,
          extraMax: 0.5,
          renderer: am5xy.AxisRendererY.new(root, {
            strokeOpacity: 0.0,
          }),
          numberFormatter: numberFormatter,
        }),
      );

      let yRenderer = yAxis.get('renderer');

      yRenderer.grid.template.set('forceHidden', true);
      yRenderer.labels.template.set('forceHidden', true);

      let series1 = chart.series.push(
        am5xy.ColumnSeries.new(root, {
          name: 'Income',
          xAxis: xAxis,
          yAxis: yAxis,
          valueYField: 'value',
          categoryXField: 'category',
          tooltip: am5.Tooltip.new(root, {}),
          fill: am5.color(0x3774e8),
          stroke: am5.color(0x3774e8),
        }),
      );

      var tooltip = am5.Tooltip.new(root, {
        labelText: "{valueY.formatNumber('#,##,###.#')}",
      });

      tooltip.label.setAll({
        direction: 'rtl',
      });

      series1.columns.template.setAll({
        tooltipText: '{valueY}',
        maxWidth: 35,
        width: am5.percent(50),
        tooltipY: 0,
        fill: am5.color(0x3774e8),
        tooltip: tooltip,
        cornerRadiusTL: 10,
        cornerRadiusTR: 10,
      });

      series1.bullets.push((root, series, dataItem: any) => {
        return am5.Bullet.new(root, {
          locationY: 0.97,
          sprite: am5.Label.new(root, {
            text: this.reportService.formatInternationNum(
              dataItem.get('valueY'),
            ),
            fill: am5.color('#3774E8'),
            centerX: am5.p50,
            centerY: 30, // Position above the bar
            populateText: true,
            fontSize: 10,
          }),
        });
      });
      series1.data.setAll(data);

      this.root = root;
    });
  }

  // pie Chart
  pieChart() {
    this.browserOnly(() => {
      if (this.root) {
        this.root.dispose();
      }
      let root = am5.Root.new(`${this.name}`);

      root.setThemes([am5themes_Animated.new(root)]);

      let chart = root.container.children.push(
        am5percent.PieChart.new(root, {
          layout: root.horizontalLayout,
        }),
      );

      // Define data
      var data = this.data;
      let itemLength = data?.length;
      // Create series
      var series = chart.series.push(
        am5percent.PieSeries.new(root, {
          name: 'Series',
          valueField: 'value',
          categoryField: 'category',
          legendLabelText: '[bold]{category}[/]',
          legendValueText: '',
          alignLabels: false,
        }),
      );

      series.set(
        'colors',
        am5.ColorSet.new(root, {
          colors: [
            am5.color('#2372F0'),
            am5.color('#9BB9F3'),
            am5.color('#568AEE'),
            am5.color('#87ADF4'),
            am5.color('#AFC7F6'),
            am5.color('#CDDEFF'),
          ],
          reuse: true, // Reuse colors if more slices exist
        }),
      );

      series.data.setAll(data);
      // series.labels.template.setAll({
      //   text: "[bold]{valuePercentTotal.formatNumber('0.00')}",
      //   fontSize: 9,
      //   fill: am5.color(0xFFFFFF),
      //   radius: -50
      // });
      series.ticks.template.set('forceHidden', true);

      if (this.is_v2_report) {
        series.slices.template.set(
          'tooltipText',
          '[bold]{category}:[/] {value}',
        );

        series.labels.template.setAll({
          text: "{valuePercentTotal.formatNumber('#.#')}%", // Show percentage
          fontSize: 12,
          fill: am5.color(0xffffff),
          radius: -60,
        });
      } else {
        series.slices.template.set(
          'tooltipText',
          "[bold]{category}:[/] {valuePercentTotal.formatNumber('#.#')}%",
        );

        series.labels.template.set('forceHidden', true);
      }

      // Add legend
      let strLength = data.some((x: any) => x['category'].length > 10);
      let legend;
      if (itemLength > 10) {
        legend = chart.children.push(
          am5.Legend.new(root, {
            centerY: am5.percent(30),
            y: am5.percent(45),
            x: am5.percent(60),
            layout: root.verticalLayout,
            // height: am5.percent(80),
            maxHeight: 230,
            verticalScrollbar: am5.Scrollbar.new(root, {
              orientation: 'vertical',
              animationDuration: 0,
            }),
          }),
        );
      } else {
        legend = chart.children.push(
          am5.Legend.new(root, {
            centerY: am5.percent(30),
            y: am5.percent(45),
            x: am5.percent(strLength ? (this.lang === 2 ? 68 : 58) : 63),
            layout: root.verticalLayout,
            maxHeight: 230,
          }),
        );
      }
      // legend.itemContainers.template.setAll({
      //   reverseChildren: this.lang === 1 ? false : true,
      // });

      legend.markers.template.setAll({
        width: 11,
        height: 11,
      });
      legend.labels.template.setAll({
        fontSize: 11,
        maxWidth: 170,
        oversizedBehavior: 'wrap',
      });
      legend.valueLabels.template.setAll({
        fontSize: 11,
      });

      legend.data.setAll(series.dataItems);

      this.root = root;
    });
  }

  // bar Stacked Chart
  barStackedChart() {
    this.browserOnly(() => {
      if (this.root) {
        this.root.dispose();
      }
      let root = am5.Root.new(`${this.name}`);

      root.setThemes([am5themes_Animated.new(root)]);

      let chart = root.container.children.push(
        am5xy.XYChart.new(root, {
          panX: false,
          panY: false,
          layout: root.verticalLayout,
          arrangeTooltips: false,
          paddingLeft: 0,
          paddingRight: 10,
        }),
      );

      // Use only absolute numbers
      chart.getNumberFormatter().set('numberFormat', '#.#s');

      let data = this.data;

      let yAxis = chart.yAxes.push(
        am5xy.CategoryAxis.new(root, {
          categoryField: 'category',
          renderer: am5xy.AxisRendererY.new(root, {
            inversed: true,
            cellStartLocation: 0.1,
            cellEndLocation: 0.9,
            minorGridEnabled: true,
            minGridDistance: 10,
          }),
        }),
      );

      yAxis.data.setAll(data);

      let xAxis = chart.xAxes.push(
        am5xy.ValueAxis.new(root, {
          renderer: am5xy.AxisRendererX.new(root, {
            minGridDistance: 60,
            strokeOpacity: 0.1,
          }),
        }),
      );

      let yRenderer = yAxis.get('renderer');

      yRenderer.grid.template.set('forceHidden', true);
      yRenderer.labels.template.set('fontSize', 11);

      let xRenderer = xAxis.get('renderer');

      xRenderer.grid.template.set('forceHidden', true);
      xRenderer.labels.template.set('forceHidden', true);

      function createSeries(
        field: any,
        labelCenterX: any,
        pointerOrientation: any,
        rangeValue: any,
        rowIndex: any,
      ) {
        let series = chart.series.push(
          am5xy.ColumnSeries.new(root, {
            xAxis: xAxis,
            yAxis: yAxis,
            valueXField: field,
            categoryYField: 'category',
            sequencedInterpolation: true,
            clustered: false,
            tooltip: am5.Tooltip.new(root, {
              pointerOrientation: pointerOrientation,
              labelText: '{categoryY}: {valueX}',
            }),
            fill: rowIndex === '1' ? am5.color(0x3774e8) : am5.color(0xee7435),
            stroke:
              rowIndex === '1' ? am5.color(0x3774e8) : am5.color(0xee7435),
          }),
        );

        series.columns.template.setAll({
          height: am5.p100,
          width: am5.p50,
        });

        series.bullets.push(function () {
          return am5.Bullet.new(root, {
            sprite: am5.Label.new(root, {
              centerY: am5.p50,
              text: '{valueX}',
              populateText: true,
              centerX: labelCenterX,
              fontSize: 11,
            }),
          });
        });

        series.data.setAll(data);
        series.appear();

        return series;
      }

      createSeries('value', am5.p100, 'right', -3, '1');
      createSeries('value1', 0, 'left', 4, '2');
      let cursor = chart.set(
        'cursor',
        am5xy.XYCursor.new(root, {
          behavior: 'zoomY',
        }),
      );
      cursor.lineY.set('forceHidden', true);
      cursor.lineX.set('forceHidden', true);

      var legend = chart.children.push(
        am5.Legend.new(root, {
          nameField: 'name',
          fillField: 'color',
          strokeField: 'color',
          centerX: am5.percent(60),
          x: am5.percent(60),
        }),
      );

      legend.data.setAll([
        {
          name: this.translate.instant('reports.male'),
          color: am5.color(0x3774e8),
        },
        {
          name: this.translate.instant('reports.female'),
          color: am5.color(0xee7435),
        },
      ]);

      legend.markers.template.setAll({
        width: 40,
        height: 8,
      });
      legend.labels.template.setAll({
        fontSize: 11,
      });
      legend.valueLabels.template.setAll({
        fontSize: 11,
      });
      //chart.appear(1000, 100);
      this.root = root;
    });
  }

  // multi Bar line Chart
  multiBarLineGroupChart() {}

  // Bar line Group Chart
  barLineGroupChart() {}

  // normal Bar line Chart
  normalBarLineChart() {
    this.browserOnly(() => {
      if (this.root) {
        this.root.dispose();
      }
      let root = am5.Root.new(`${this.name}`);

      root.setThemes([am5themes_Animated.new(root)]);

      let chart = root.container.children.push(
        am5xy.XYChart.new(root, {
          panX: false,
          panY: false,
          wheelX: 'panX',
          wheelY: 'zoomX',
          paddingLeft: 0,
          layout: root.verticalLayout,
        }),
      );

      chart.zoomOutButton.set('forceHidden', true);

      let data = this.data;

      let xRenderer = am5xy.AxisRendererX.new(root, {
        minorGridEnabled: true,
        minGridDistance: 60,
      });
      let xAxis = chart.xAxes.push(
        am5xy.CategoryAxis.new(root, {
          categoryField: 'category',
          renderer: xRenderer,
          tooltip: am5.Tooltip.new(root, {}),
        }),
      );
      xRenderer.grid.template.setAll({
        location: 1,
        forceHidden: true,
      });

      xAxis.data.setAll(data);

      let yAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          min: 0,
          extraMax: 0.5,
          renderer: am5xy.AxisRendererY.new(root, {
            strokeOpacity: 0.1,
          }),
        }),
      );

      let yRenderer = yAxis.get('renderer');

      yRenderer.grid.template.set('forceHidden', true);
      yRenderer.labels.template.set('forceHidden', true);

      let series1 = chart.series.push(
        am5xy.ColumnSeries.new(root, {
          name: 'Occupied Area (sqm)',
          xAxis: xAxis,
          yAxis: yAxis,
          valueYField: 'value1',
          categoryXField: 'category',
          tooltip: am5.Tooltip.new(root, {
            pointerOrientation: 'horizontal',
            labelText: '{name} in {categoryX}: {valueY}',
          }),
          fill: am5.color(0x3774e8),
          stroke: am5.color(0x3774e8),
        }),
      );

      // series1.columns.template.setAll({
      //   tooltipY: am5.percent(10),
      //   templateField: "columnSettings"
      // });

      series1.data.setAll(data);

      let series2 = chart.series.push(
        am5xy.LineSeries.new(root, {
          name: 'Occupied Ratio (%)',
          xAxis: xAxis,
          yAxis: yAxis,
          valueYField: 'total',
          categoryXField: 'category',
          tooltip: am5.Tooltip.new(root, {
            pointerOrientation: 'horizontal',
            labelText:
              "{name} in {categoryX}: {valueY.formatNumber('#,##,###.#')}",
          }),
          fill: am5.color(0xf58750),
          stroke: am5.color(0xf58750),
          legendValueText: "{valueY.formatNumber('#,##,###.#')}",
        }),
      );

      // series2.strokes.template.setAll({
      //   strokeWidth: 3,
      //   templateField: "strokeSettings"
      // });

      series2.data.setAll(data);

      series2.bullets.push(function () {
        return am5.Bullet.new(root, {
          sprite: am5.Circle.new(root, {
            strokeWidth: 3,
            radius: 5,
            fill: am5.color(0xf58750),
            stroke: am5.color(0xf58750),
          }),
        });
      });

      chart.set('cursor', am5xy.XYCursor.new(root, {}));

      let legend = chart.children.push(
        am5.Legend.new(root, {
          centerX: am5.p50,
          x: am5.p50,
        }),
      );

      legend.markers.template.setAll({
        width: 50,
        height: 11,
      });
      legend.labels.template.setAll({
        fontSize: 11,
      });
      legend.valueLabels.template.setAll({
        fontSize: 11,
      });
      legend.data.setAll(chart.series.values);
      chart.appear(1000, 100);
      series1.appear();

      this.root = root;
    });
  }

  varianceIndicatorsChart() {
    this.browserOnly(() => {
      if (this.root) {
        this.root.dispose();
      }
      let root = am5.Root.new(`${this.name}`);

      root.setThemes([am5themes_Animated.new(root)]);

      let chart: any = root.container.children.push(
        am5xy.XYChart.new(root, {
          panX: false,
          panY: false,
          wheelX: 'none',
          wheelY: 'none',
          layout: root.verticalLayout,
          paddingLeft: 10,
          paddingRight: 10,
        }),
      );

      // Data
      let data: any = this.data;

      var numberFormatter = am5.NumberFormatter.new(root, {
        numberFormat: '#.#',
      });
      for (var i = 0; i < data.length; i++) {
        data[i].formattedValue = numberFormatter.format(data[i].value);
      }

      // Populate data
      for (var i = 0; i < data.length - 1; i++) {
        data[i].valueNext = data[i + 1].value;
      }

      let xRenderer = am5xy.AxisRendererX.new(root, {
        cellStartLocation: 0.35,
        cellEndLocation: 0.9,
        minGridDistance: 30,
        minorGridEnabled: true,
      });

      let xAxis = chart.xAxes.push(
        am5xy.CategoryAxis.new(root, {
          categoryField: 'category',
          renderer: xRenderer,
          tooltip: am5.Tooltip.new(root, {}),
        }),
      );

      xRenderer.grid.template.setAll({
        location: 1,
        forceHidden: true,
      });

      xAxis.data.setAll(data);

      let yAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          min: 0,
          extraMax: 0.3,
          renderer: am5xy.AxisRendererY.new(root, {
            strokeOpacity: 0.1,
          }),
          numberFormatter: numberFormatter,
        }),
      );

      let yRenderer = yAxis.get('renderer');
      yRenderer.grid.template.set('forceHidden', true);

      let series = chart.series.push(
        am5xy.ColumnSeries.new(root, {
          xAxis: xAxis,
          yAxis: yAxis,
          valueYField: 'value',
          categoryXField: 'category',
          fill: am5.color('#3774E8'),
          width: 50,
        }),
      );

      series.columns.template.setAll({
        // tooltipText: "[#000000][bold]{category}:{valueY}",
        tooltipText: '[#000000][bold]{valueY}',
        width: 35,
        tooltipY: -4,
        cornerRadiusTL: 8,
        cornerRadiusTR: 8,
        tooltip: am5.Tooltip.new(root, {
          getFillFromSprite: false, // Disable getting color from series
          getStrokeFromSprite: false, // Enable getting stroke color from series
        }),
      });

      // Customize tooltip background, border radius, and font size
      let tooltip = series.columns.template.get('tooltip');
      tooltip.get('background').setAll({
        fill: am5.color('#C3D5F8'), // Change to desired background color
        strokeWidth: 0,
        cornerRadiusTL: 10, // Set top-left border radius
        cornerRadiusTR: 10, // Set top-right border radius
        cornerRadiusBL: 10, // Set bottom-left border radius
        cornerRadiusBR: 10, // Set bottom-right border radius
      });

      tooltip.label.setAll({
        fontSize: 12, // Change to desired font size
      });
      // chart.get("colors").set("colors", [
      //   am5.color('#3774E8'),
      //   am5.color('#3774E8'),
      //   am5.color('#3774E8'),
      //   am5.color('#3774E8'),
      //   am5.color('#3774E8')
      // ]);
      // series.columns.template.adapters.add("fill", function (fill: any, target: any) {
      //   return chart.get("colors").getIndex(series.columns.indexOf(target));
      // });
      series.data.setAll(data);
      // Variance indicator series
      let series2 = chart.series.push(
        am5xy.ColumnSeries.new(root, {
          xAxis: xAxis,
          yAxis: yAxis,
          valueYField: 'valueNext',
          openValueYField: 'value',
          categoryXField: 'category',
          fill: am5.color(0x3774e8),
          stroke: am5.color(0x3774e8),
          locationY: 0.1,
          locationX: 0.7,
          fontWeight: '300',
          fontSize: 12,
        }),
      );

      // Add centerY adapter to series2
      series2.columns.template.setAll({
        centerY: 0, // Set initial centerY value
      });

      series2.columns.template.adapters.add(
        'centerY',
        function (centerY: any, target: any) {
          var percent = getVariancePercent(target.dataItem);
          return percent <= -85 ? 30 : 0; // Adjust centerY based on the variance percentage
        },
      );

      series2.columns.template.setAll({
        width: 0,
        fill: am5.color('#000000'),
        stroke: am5.color('#000000'),
      });
      series2.data.setAll(data);
      series2.bullets.push(function (root: any, series2: any, dataItem: any) {
        let percent = getVariancePercent(dataItem);

        if (percent === 0) {
          return null; // Hide arrow if no change
        }
        let arrow = am5.Graphics.new(root, {
          rotation: percent < 0 ? 90 : -90,
          centerX: am5.p50,
          centerY: am5.p50,
          dy: 0,
          dx: 0,
          fill: am5.color('#000000'),
          stroke: am5.color('#000000'),
          draw: function (display) {
            display.moveTo(0, -3);
            display.lineTo(8, 0);
            display.lineTo(0, 3);
            display.lineTo(0, -3);
          },
        });
        //arrow modifys
        arrow.adapters.add('dy', (dy: any, target: any) => {
          var percent = getVariancePercent(target.dataItem);
          if (percent <= -80) {
            return -30; // Adjust this value to move the arrow further down
          }
          return;
        });

        return am5.Bullet.new(root, {
          locationY: 1,
          locationX: 0.5,
          sprite: arrow,
        });
      });

      series2.bullets.push((root: any, series2: any, dataItem: any) => {
        let percent = getVariancePercent(dataItem);
        let label = am5.Label.new(root, {
          text:
            percent > 0
              ? '+' + this.decimalPipe.transform(percent, '1.0-1')
              : percent.toString(),
          fontWeight: '300',
          fill:
            percent < 0
              ? am5.color('#FF6C16')
              : percent === 0
                ? am5.color('#EFBA00')
                : am5.color('#57AC52'),
          fontSize: 12,
          centerY: percent < 0 ? am5.p0 : am5.p100,
          centerX: am5.p50,
          populateText: true,
        });
        //Label Modify
        label.adapters.add('centerY', function (center, target) {
          var percent = getVariancePercent(target.dataItem);
          return percent <= -85 ? am5.p100 : percent < 0 ? am5.p0 : center;
        });

        return am5.Bullet.new(root, {
          locationY: 1,
          locationX: -4,
          sprite: label,
        });
      });

      series.appear(1000);
      chart.appear(1000, 100);

      function getVariancePercent(dataItem: any) {
        if (dataItem) {
          let value = dataItem.get('valueY');
          let openValue = dataItem.get('openValueY');
          let change = value - openValue;
          return Math.round(change);
        }
        return 0;
      }

      this.root = root;
    });
  }

  smallBarChart() {
    this.browserOnly(() => {
      if (this.root) {
        this.root.dispose();
      }
      let root = am5.Root.new(`${this.name}`);

      root.setThemes([am5themes_Animated.new(root)]);

      let chart = root.container.children.push(
        am5xy.XYChart.new(root, {
          panX: false,
          panY: false,
          wheelX: 'none',
          wheelY: 'none',
          paddingBottom: 40,
          paddingTop: 0,
          paddingLeft: 0,
          paddingRight: 0,
          layout: root.verticalLayout,
          pinchZoomX: false,
        }),
      );

      // Define data
      let data = this.data;
      var numberFormatter = am5.NumberFormatter.new(root, {
        numberFormat: '#.#',
      });
      for (var i = 0; i < data.length; i++) {
        data[i].formattedValue = numberFormatter.format(data[i].value);
      }

      // Pad to 5 with empty transparent bars
      let fullSlotCount = data.length === 2 ? 4 : 5;
      if (data.length < 4) {
        const emptyCount = fullSlotCount - data.length;
        const leftPadding = Math.floor(emptyCount / 2);
        const rightPadding = emptyCount - leftPadding;

        for (let i = 0; i < leftPadding; i++) {
          data.unshift({
            category: '',
            value: null,
            formattedValue: '',
            empty: true,
          });
        }
        for (let i = 0; i < rightPadding; i++) {
          data.push({
            category: '',
            value: null,
            formattedValue: '',
            empty: true,
          });
        }
      }

      let xRenderer = am5xy.AxisRendererX.new(root, {
        minorGridEnabled: true,
        minGridDistance: 30,
      });
      let xAxis = chart.xAxes.push(
        am5xy.CategoryAxis.new(root, {
          categoryField: 'category',
          renderer: xRenderer,
          tooltip: am5.Tooltip.new(root, {}),
        }),
      );
      xRenderer.grid.template.setAll({
        location: 1,
        forceHidden: true,
      });

      xRenderer.labels.template.setAll({
        fill: am5.color('#6C737F'),
        fontWeight: '500',
        fontSize: 10,
        fontFamily: 'Manrope',
      });

      xAxis.data.setAll(data);

      let yAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          min: 0,
          // maxDeviation: 0.3,
          extraMax: 0.2,
          renderer: am5xy.AxisRendererY.new(root, {
            strokeOpacity: 0,
          }),
          numberFormatter: numberFormatter,
        }),
      );

      let yRenderer = yAxis.get('renderer');

      yRenderer.grid.template.set('forceHidden', true);
      yRenderer.labels.template.set('forceHidden', true);

      let series1 = chart.series.push(
        am5xy.ColumnSeries.new(root, {
          name: this.name,
          xAxis: xAxis,
          yAxis: yAxis,
          valueYField: 'value',
          categoryXField: 'category',
          tooltip: am5.Tooltip.new(root, {}),
          fill: am5.color(0x3774e8),
          stroke: am5.color(0x3774e8),
          width: 35,
        }),
      );

      var tooltip = am5.Tooltip.new(root, {
        labelText: "{valueY.formatNumber('#,##,###.#')}",
      });

      series1.columns.template.setAll({
        tooltipText: '{valueY}',
        maxWidth: 40,
        width: am5.percent(90),
        tooltipY: 0,
        fill: am5.color(0x3774e8),
        tooltip: tooltip,
        cornerRadiusTL: 10,
        cornerRadiusTR: 10,
        fillOpacity: this.index === 0 ? 1 : Math.abs(1 - this.index * 0.1),
      });

      series1.bullets.push((root, series, dataItem: any) => {
        return am5.Bullet.new(root, {
          locationY: 0.97,
          sprite: am5.Label.new(root, {
            text: this.reportService.formatInternationNum(
              dataItem.dataContext.formattedValue,
            ), // Bind dynamic value here
            fill: am5.color('#3774E8'),
            centerX: am5.p50,
            centerY: 30, // Position above the bar
            populateText: true,
            fontSize: 10,
            fontFamily: 'Manrope',
          }),
        });
      });
      let label = chart.children.unshift(
        am5.Label.new(root, {
          text: this.name,
          x: am5.percent(50), // Center horizontally
          y: am5.percent(100), // Place at the bottom
          centerX: am5.percent(50), // Ensure proper centering
          dy: 15, // Adjust vertical position (increase to move further down)
          fontSize: 12,
          fill: am5.color('#000000'), // Gray color
          fontWeight: '600',
          fontFamily: 'Manrope',
          direction: this.lang === 2 ? 'rtl' : 'ltr',
          oversizedBehavior: 'truncate', // adds ellipsis
          maxWidth: 315,
        }),
      );

      label.set(
        'tooltip',
        am5.Tooltip.new(root, {
          labelText: this.name, // full text in tooltip
        }),
      );

      series1.data.setAll(data);
      this.root = root;
    });
  }

  smallvarianceIndicatorsChart() {
    this.browserOnly(() => {
      if (this.root) {
        this.root.dispose();
      }
      let root = am5.Root.new(`${this.name}`);

      root.setThemes([am5themes_Animated.new(root)]);

      let chart: any = root.container.children.push(
        am5xy.XYChart.new(root, {
          panX: false,
          panY: false,
          wheelX: 'none',
          wheelY: 'none',
          paddingBottom: 50,
          paddingTop: 0,
          paddingLeft: 0,
          paddingRight: 0,
          layout: root.verticalLayout,
        }),
      );

      // Data
      let data: any = this.data;

      var numberFormatter = am5.NumberFormatter.new(root, {
        numberFormat: '#.#',
      });
      for (var i = 0; i < data.length; i++) {
        data[i].formattedValue = numberFormatter.format(data[i].value);
      }

      // Populate data
      for (var i = 0; i < data.length - 1; i++) {
        data[i].valueNext = data[i + 1].value;
      }

      let xRenderer = am5xy.AxisRendererX.new(root, {
        cellStartLocation: 0.35,
        cellEndLocation: 0.9,
        minGridDistance: 40,
        minorGridEnabled: true,
      });

      let xAxis = chart.xAxes.push(
        am5xy.CategoryAxis.new(root, {
          categoryField: 'category',
          renderer: xRenderer,
          tooltip: am5.Tooltip.new(root, {}),
        }),
      );

      xRenderer.grid.template.setAll({
        location: 1,
        forceHidden: true,
      });

      xAxis.data.setAll(data);

      let yAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          min: 0,
          extraMax: 0.3,
          renderer: am5xy.AxisRendererY.new(root, {
            strokeOpacity: 0,
          }),
          numberFormatter: numberFormatter,
        }),
      );

      let yRenderer = yAxis.get('renderer');
      yRenderer.grid.template.set('forceHidden', true);
      yRenderer.labels.template.set('forceHidden', true);

      let series = chart.series.push(
        am5xy.ColumnSeries.new(root, {
          xAxis: xAxis,
          yAxis: yAxis,
          valueYField: 'value',
          categoryXField: 'category',
          fill: am5.color('#3774E8'),
          width: 50,
        }),
      );

      series.columns.template.setAll({
        // tooltipText: "[#000000][bold]{category}:{valueY}",
        tooltipText: '[#000000][bold]{valueY}',
        width: 35,
        tooltipY: -4,
        cornerRadiusTL: 8,
        cornerRadiusTR: 8,
        tooltip: am5.Tooltip.new(root, {
          getFillFromSprite: false, // Disable getting color from series
          getStrokeFromSprite: false, // Enable getting stroke color from series
        }),
      });

      // Customize tooltip background, border radius, and font size
      let tooltip = series.columns.template.get('tooltip');
      tooltip.get('background').setAll({
        fill: am5.color('#C3D5F8'), // Change to desired background color
        strokeWidth: 0,
        cornerRadiusTL: 10, // Set top-left border radius
        cornerRadiusTR: 10, // Set top-right border radius
        cornerRadiusBL: 10, // Set bottom-left border radius
        cornerRadiusBR: 10, // Set bottom-right border radius
      });

      tooltip.label.setAll({
        fontSize: 12, // Change to desired font size
      });
      // chart.get("colors").set("colors", [
      //   am5.color('#3774E8'),
      //   am5.color('#3774E8'),
      //   am5.color('#3774E8'),
      //   am5.color('#3774E8'),
      //   am5.color('#3774E8')
      // ]);
      // series.columns.template.adapters.add("fill", function (fill: any, target: any) {
      //   return chart.get("colors").getIndex(series.columns.indexOf(target));
      // });
      series.data.setAll(data);
      // Variance indicator series
      let series2 = chart.series.push(
        am5xy.ColumnSeries.new(root, {
          xAxis: xAxis,
          yAxis: yAxis,
          valueYField: 'valueNext',
          openValueYField: 'value',
          categoryXField: 'category',
          fill: am5.color(0x3774e8),
          stroke: am5.color(0x3774e8),
          locationY: 0.1,
          locationX: 0.7,
          fontWeight: '300',
          fontSize: 12,
        }),
      );

      // Add centerY adapter to series2
      series2.columns.template.setAll({
        centerY: 0, // Set initial centerY value
      });

      series2.columns.template.adapters.add(
        'centerY',
        function (centerY: any, target: any) {
          var percent = getVariancePercent(target.dataItem);
          return percent <= -85 ? 30 : 0; // Adjust centerY based on the variance percentage
        },
      );

      series2.columns.template.setAll({
        width: 0,
        fill: am5.color('#000000'),
        stroke: am5.color('#000000'),
      });
      series2.data.setAll(data);
      series2.bullets.push(function (root: any, series2: any, dataItem: any) {
        let percent = getVariancePercent(dataItem);

        if (percent === 0) {
          return null; // Hide arrow if no change
        }
        let arrow = am5.Graphics.new(root, {
          rotation: percent < 0 ? 90 : -90,
          centerX: am5.p50,
          centerY: am5.p50,
          dy: 0,
          dx: 0,
          fill: am5.color('#000000'),
          stroke: am5.color('#000000'),
          draw: function (display) {
            display.moveTo(0, -3);
            display.lineTo(8, 0);
            display.lineTo(0, 3);
            display.lineTo(0, -3);
          },
        });
        //arrow modifys
        arrow.adapters.add('dy', (dy: any, target: any) => {
          var percent = getVariancePercent(target.dataItem);
          if (percent <= -90) {
            return -30; // Adjust this value to move the arrow further down
          }
          return;
        });

        return am5.Bullet.new(root, {
          locationY: 1,
          locationX: 0.5,
          sprite: arrow,
        });
      });

      series2.bullets.push((root: any, series2: any, dataItem: any) => {
        let percent = getVariancePercent(dataItem);
        let label = am5.Label.new(root, {
          text:
            percent > 0
              ? '+' + this.decimalPipe.transform(percent, '1.0-1')
              : percent.toString(),
          fontWeight: '300',
          fill:
            percent < 0
              ? am5.color('#FF6C16')
              : percent === 0
                ? am5.color('#EFBA00')
                : am5.color('#57AC52'),
          fontSize: 12,
          centerY: percent < 0 ? am5.p0 : am5.p100,
          centerX: am5.p50,
          populateText: true,
        });

        label.adapters.add('text', function (text, target) {
          let percent = getVariancePercent(target.dataItem);
          return percent ? percent + '%' : text;
        });
        //Label Modify
        label.adapters.add('centerY', function (center, target) {
          var percent = getVariancePercent(target.dataItem);
          return percent <= -85 ? am5.p100 : percent < 0 ? am5.p0 : center;
        });

        return am5.Bullet.new(root, {
          locationY: 1,
          locationX: -4,
          sprite: label,
        });
      });

      chart.children.unshift(
        am5.Label.new(root, {
          text: this.text,
          fontSize: 16,
          fontWeight: '500',
          textAlign: 'center',
          x: am5.percent(50),
          centerX: am5.percent(50),
          y: am5.percent(100),
        }),
      );

      series.appear(1000);
      chart.appear(1000, 100);

      function getVariancePercent(dataItem: any) {
        if (dataItem) {
          let value = dataItem.get('valueY');
          let openValue = dataItem.get('openValueY');
          let change = value - openValue;
          if (change === 0) {
            return 0;
          }
          let percentage = Math.round((change / openValue) * 100);

          return isFinite(percentage) ? percentage : 100;
        }
        return 0;
      }

      this.root = root;
    });
  }

  projectAnalysisChart() {
    this.browserOnly(() => {
      if (this.root) {
        this.root.dispose();
      }

      let root = am5.Root.new(`${this.name}`);
      root.setThemes([am5themes_Animated.new(root)]);

      let chart = root.container.children.push(
        am5xy.XYChart.new(root, {
          panX: true,
          panY: false,
          wheelX: 'panX',
          wheelY: 'zoomX',
          paddingBottom: this.staticZoning ? 20 : 80,
          paddingTop: 10,
          paddingLeft: 50,
          paddingRight: 40,
          layout: root.verticalLayout,
          pinchZoomX: true,
        }),
      );

      var numberFormatter = am5.NumberFormatter.new(root, {
        numberFormat: '#.#',
      });

      // Data is direct array with {category: 'Q3-2021', value: number}
      const quarterlyData = this.data || [];

      if (quarterlyData.length === 0) {
        this.root = root;
        return;
      }

      // Group by year
      const groupedByYear = this.groupDataByYear(quarterlyData);

      // Auto expand first (oldest) year ONLY on initial load (when no expanded years yet)
      if (this.expandedYears.size === 0 && groupedByYear.length > 0) {
        this.expandedYears.add(groupedByYear[0].year);
      }

      const finalData: any[] = [];

      groupedByYear.forEach((group) => {
        const year = group.year;
        const shortYear = year.slice(-2);

        // Year level value (last quarter's value for line connection)
        const yearValue = group.items[group.items.length - 1]?.value ?? null;

        finalData.push({
          label: year,
          value: yearValue,
          year: year,
          isYear: true,
        });

        // Quarters if expanded
        if (this.expandedYears.has(year)) {
          group.items.forEach((item: any) => {
            const q = item.quarter; // Already "Q3"
            finalData.push({
              label: `${q}'${shortYear}`,
              value: item.value,
              year: year,
              isYear: false,
            });
          });
        }
      });

      let xRenderer = am5xy.AxisRendererX.new(root, {
        minGridDistance: 50,
        cellStartLocation: 0.2,
        cellEndLocation: 0.8,
      });

      this.xAxis = chart.xAxes.push(
        am5xy.CategoryAxis.new(root, {
          categoryField: 'label',
          renderer: xRenderer,
        }),
      );

      this.xAxis.get('renderer').labels.template.setAll({
        rotation: -15,
        centerY: am5.p50,
        centerX: am5.p50,
        paddingRight: 10,
        fontSize: 12,
        fill: am5.color(0x555555),
        oversizedBehavior: 'fit',
        textAlign: 'center',
        minScale: 0.5,
        forceHidden: false,
      });

      this.xAxis
        .get('renderer')
        .labels.template.adapters.add('text', (text: any, target: any) => {
          const data = target.dataItem?.dataContext as any;
          if (data?.isYear) {
            target.set('fontWeight', '700');
            target.set('fontSize', 14);
          } else {
            target.set('fontWeight', '400');
            target.set('fontSize', 11);
          }
          target.set('forceHidden', false);
          return text;
        });

      let yAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          min: 0,
          extraMax: 0.2,
          renderer: am5xy.AxisRendererY.new(root, { strokeOpacity: 0 }),
          numberFormatter: numberFormatter,
        }),
      );

      yAxis.get('renderer').grid.template.setAll({
        stroke: am5.color(0xf5f5f5),
        strokeOpacity: 1,
        visible: true,
      });

      xRenderer.grid.template.set('visible', false);

      chart.children.unshift(
        am5.Label.new(root, {
          text: `${this.CurrencyCode}/sqm`,
          x: 5,
          y: -8,
          fontWeight: '400',
          fontSize: 12,
          fill: am5.color(0x555555),
        }),
      );

      // Single Line Series
      this.series = chart.series.push(
        am5xy.LineSeries.new(root, {
          name: this.label,
          xAxis: this.xAxis,
          yAxis: yAxis,
          valueYField: 'value',
          categoryXField: 'label',
          stroke: am5.color(0x3774e8),
          tooltip: am5.Tooltip.new(root, {
            labelHTML: `<div class='text_tooltip_white'>{valueY.formatNumber('#,##,###.#')} ${this.CurrencyCode}/sqm</div>`,
          }),
        }),
      );
      this.series
        .get('tooltip')
        ?.get('background')
        ?.setAll({
          fill: am5.color(0x3774e8),
          stroke: am5.color(0x3774e8),
          fillOpacity: 1,
        });

      this.series.get('tooltip')?.label.setAll({
        fill: am5.color(0xffffff), // text white
      });
      // Thick line for visibility
      this.series.strokes.template.set('strokeWidth', 1);

      // this.series.appear(1000);

      // Bullets (dots) - NO tooltip on dots to avoid overlap
      this.series.bullets.push((root, s, dataItem: any) => {
        const ctx = dataItem.dataContext as any;

        if (ctx.isYear) {
          const container = am5.Container.new(root, { interactive: true });

          const isExpanded = this.expandedYears.has(ctx.year);

          container.children.push(
            am5.Circle.new(root, {
              radius: isExpanded ? 9 : 7,
              fill: isExpanded ? am5.color(0x000000) : am5.color(0x3774e8),
              stroke: am5.color(0x3774e8),
              strokeWidth: 2,
            }),
          );

          container.events.on('click', () =>
            this.toggleYearExpansion(ctx.year),
          );
          container.set('cursorOverStyle', 'pointer');

          return am5.Bullet.new(root, { sprite: container });
        } else {
          return am5.Bullet.new(root, {
            sprite: am5.Circle.new(root, {
              radius: 4,
              fill: am5.color(0x3774e8),
            }),
          });
        }
      });

      this.series.data.setAll(finalData);
      this.xAxis.data.setAll(finalData);

      // Legend
      if (!this.staticZoning) {
        const legend = chart.children.push(
          am5.Legend.new(root, {
            centerX: am5.p50,
            x: am5.p50,
            layout: root.horizontalLayout,
            y: am5.percent(100),
          }),
        );

        legend.data.setAll([this.series]);
      }

      // Cursor with vertical line on hover + tooltip from series (nearest point)
      let cursor = chart.set(
        'cursor',
        am5xy.XYCursor.new(root, {
          behavior: 'none',
        }),
      );
      cursor.lineX.setAll({
        visible: true,
        strokeOpacity: 0.3,
        strokeDasharray: [4, 4],
      });
      cursor.lineY.set('visible', false);

      this.root = root;
    });
  }
  private buildFinalData(): any[] {
    const grouped = this.groupDataByYear(this.data || []);
    const finalData: any[] = [];

    grouped.forEach((group) => {
      const year = group.year;
      const shortYear = year.slice(-2);

      const yearValue = group.items[group.items.length - 1]?.value ?? null;

      finalData.push({
        label: year,
        value: yearValue,
        year,
        isYear: true,
      });

      if (this.expandedYears.has(year)) {
        group.items.forEach((item: any) => {
          finalData.push({
            label: `${item.quarter}'${shortYear}`,
            value: item.value,
            year,
            isYear: false,
          });
        });
      }
    });

    return finalData;
  }

  private toggleYearExpansion(year: string) {
    if (this.expandedYears.has(year)) {
      this.expandedYears.delete(year);
    } else {
      this.expandedYears.add(year);
    }

    const finalData = this.buildFinalData();
    this.series.data.setAll(finalData);
    this.xAxis.data.setAll(finalData);
  }
  resetExpandedYears() {
    this.expandedYears.clear();
    const finalData = this.buildFinalData();
    this.series.data.setAll(finalData);
    this.xAxis.data.setAll(finalData);
  }

  private groupDataByYear(data: any[]) {
    const groups: any[] = [];

    data.forEach((item) => {
      if (item && item.category) {
        const parts = item.category.split('-');
        if (parts.length === 2) {
          const q = parts[0]; // Q3
          const year = parts[1]; // 2021
          let group = groups.find((g) => g.year === year);

          if (!group) {
            group = { year, items: [] };
            groups.push(group);
          }

          group.items.push({
            quarter: q,
            value: item.value,
          });
        }
      }
    });

    // Sort groups by year ascending
    groups.sort((a, b) => a.year.localeCompare(b.year));

    // Sort quarters within each group
    const quarterOrder = { Q1: 1, Q2: 2, Q3: 3, Q4: 4 } as const;
    groups.forEach((group) => {
      group.items.sort((a: any, b: any) => {
        const qA = a.quarter as keyof typeof quarterOrder;
        const qB = b.quarter as keyof typeof quarterOrder;
        return (quarterOrder[qA] || 5) - (quarterOrder[qB] || 5);
      });
    });

    return groups;
  }
  get hasExpandedYears(): boolean {
    return this.expandedYears.size > 0;
  }

  ngOnDestroy() {
    // Clean up root element when the component is removed
    this.browserOnly(() => {
      if (this.root) {
        this.root.dispose();
      }
    });
  }
}
